import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useStoreActions, useStoreState } from "easy-peasy";
import { isMobile } from "../../utils/ResponsiveUtils";
import { getSeconds } from "../../utils/timeUtils";
import DemoStage from "../DemoStage";
import Navigation from "../Navigation";
import PlayerControls from "../PlayerControls";
import SkipButton from "../SkipButton";
import StartCard from "../IntroScreen";
import EndCard from "../OutroScreen";
import FullScreenButton from "../FullScreenButton";
import MuteButton from "../MuteButton";
import TopToolbar from "../TopToolbar";

const DemoScreen = ({ content, intro, outro, product }) => {
  const CONTAINER_ID = "interactiveExpDemo";

  const containerRef = useRef(null);

  const {
    hasSizzle,
    showSizzle,
    sizzle,
    showFeatures,
    videoUrl,
    videoType,
    videoPoster,
    features,
    playerControls,
    progressBar,
    hasOutro,
    hasIntro,
  } = content;

  const isDesktop = !isMobile();
  const { isVisible } = useStoreState((state) => state.demo);
  const { isVisible: isNavVisible } = useStoreState(
    (state) => state.navigation
  );
  const setFeatures = useStoreActions(
    (actions) => actions.features.setFeatures
  );
  const { setVisible, setStartTime } = useStoreActions(
    (actions) => actions.demo
  );
  const { setNavVisibility } = useStoreActions((actions) => actions.navigation);

  const setDemoStartTime = useCallback(() => {
    if (isDesktop && hasSizzle && !showSizzle && features.length > 0) {
      setStartTime(getSeconds(features[0].start || 0));
    }
  }, [isDesktop, hasSizzle, showSizzle, features, setStartTime]);

  const showStage = () => {
    if (containerRef && containerRef.current) {
      containerRef.current.classList.remove("is-fade-out");
    }
  };

  const hideStage = () => {
    if (containerRef && containerRef.current) {
      containerRef.current.classList.add("is-fade-out");
    }
  };

  const handleStageHoverOut = () => {
    if (isNavVisible) {
      setTimeout(() => {
        setNavVisibility(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (features.length > 0) {
      setFeatures(features);
    }
    return () => {};
  }, [features, setFeatures]);

  useEffect(() => {
    if (isVisible) {
      showStage();
    } else {
      hideStage();
    }
    return () => {};
  }, [isVisible]);

  useEffect(() => {
    if (hasIntro) {
      setVisible(false);
    } else {
      setVisible(true);
    }
    return () => {};
  }, [hasIntro, setVisible]);

  useEffect(() => {
    setDemoStartTime();
    return () => {};
  }, [setDemoStartTime]);

  return (
    <>
      {hasIntro && <StartCard content={intro} product={product} />}
      <div id={CONTAINER_ID} ref={containerRef} className="is-fade-out">
        <div
          className="demo"
          onMouseLeave={handleStageHoverOut}
          onBlur={handleStageHoverOut}
        >
          <TopToolbar containerId={CONTAINER_ID}>
            <FullScreenButton containerId={CONTAINER_ID} />
            <MuteButton containerId={CONTAINER_ID} />
          </TopToolbar>
          <SkipButton containerId={CONTAINER_ID} />
          <div className="demo__inner">
            <DemoStage
              videoUrl={videoUrl}
              videoType={videoType}
              videoPoster={videoPoster}
              showStage={showStage}
              hideStage={hideStage}
              hasOutro={hasOutro}
              hasIntro={hasIntro}
              isMuted={showFeatures}
              sizzle={sizzle}
              product={product}
            />
            <Navigation progressBar={progressBar} showFeatures={showFeatures} />
            <PlayerControls
              content={playerControls}
              hasIntro={hasIntro}
              product={product}
            />
          </div>
        </div>
        <style jsx="true">
          {`
            #${CONTAINER_ID} {
              transition: all 0.5s ease-in !important;
              height: 100% !important;
              position: absolute !important;
              width: 100% !important;
            }
            #${CONTAINER_ID}.is-fade-out {
              opacity: 0 !important;
            }
            #${CONTAINER_ID} * {
              box-sizing: border-box !important;
            }
            #${CONTAINER_ID} .demo {
              position: absolute !important;
              z-index: 1 !important;
              top: 0 !important;
              left: 0 !important;

              width: 100% !important;
              height: 100% !important;
              padding-top: ${!isDesktop ? "1px" : "auto"} !important;
            }

            #${CONTAINER_ID} .demo__inner {
              position: relative !important;

              overflow: hidden !important;

              width: 100% !important;
              height: 100% !important;

              background: #333333 !important;
            }

            #${CONTAINER_ID} .demo__loading {
              position: absolute !important;
              z-index: 20 !important;
              top: 0 !important;
              left: 0 !important;

              display: flex !important;

              width: 100% !important;
              height: 100% !important;

              background-color: rgba($color: #000000, $alpha: 0.8) !important;

              align-items: center !important;
              justify-content: center !important;

              &.animate__fadeOut {
                z-index: 1 !important;
              }
            }
          `}
        </style>
      </div>
      {hasOutro && <EndCard content={outro} product={product} />}
    </>
  );
};

DemoScreen.propTypes = {
  content: PropTypes.shape({
    hasSizzle: PropTypes.bool,
    showSizzle: PropTypes.bool,
    sizzle: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
    showFeatures: PropTypes.bool,
    videoUrl: PropTypes.string,
    videoType: PropTypes.string,
    videoPoster: PropTypes.string,
    features: PropTypes.array.isRequired,
    playerControls: PropTypes.object.isRequired,
    progressBar: PropTypes.object.isRequired,
    hasOutro: PropTypes.bool.isRequired,
    hasIntro: PropTypes.bool.isRequired,
  }),
  intro: PropTypes.object,
  outro: PropTypes.object,
  product: PropTypes.object,
};

DemoScreen.defaultProps = {
  content: {
    hasSizzle: false,
    showSizzle: false,
    sizzle: {
      start: "",
      end: "",
    },
    showFeatures: true,
    videoUrl: "",
    videoType: "",
    videoPoster: "",
    features: [],
    progressBar: {},
    playerControls: {},
    hasOutro: false,
    hasIntro: false,
  },
  intro: {},
  outro: {},
  product: {},
};

export default DemoScreen;
